import * as am5 from '@amcharts/amcharts5';

import { AxisRendererX, AxisRendererY, ColumnSeries, IAxisRendererYSettings, XYChart } from '@amcharts/amcharts5/xy';
import { Am5CategoryValueXYBaseChart } from '../am5-category-value-x-y-base-chart.model';
import { Am5CategoryValueXYColumnChartConfiguration } from './am5-category-value-x-y-column-chart-configuration.model';
import { Am5CategoryValueXYColumnChartSettings } from './am5-category-value-x-y-column-chart-settings.model';
import { ChartSeries } from '../../chart-series.model';
import { DataPointModel } from '../../data-point.model';


export class Am5CategoryValueXYColumnChart extends Am5CategoryValueXYBaseChart {

    override config: Am5CategoryValueXYColumnChartConfiguration;
    override settings: Am5CategoryValueXYColumnChartSettings;

    override categoryAxisRenderer = AxisRendererY;

    valueLabelWidth: number = 0;
    valueLabelExtraMaxAdjust: number = -0.05;
    valueLabelTemplate: am5.Template<am5.Label>;

    readonly smallBarHeight = 30;
    readonly mediumBarHeight = 40;
    readonly largeBarHeight = 50;
    readonly columnWidthPadding = 10;
    readonly maxColumnWidthForRotation = 90;
    readonly categoryLabelPaddingTop = 5;
    readonly categoryLabelMaxLines = 2;
    readonly categoryLabelEllipsis = '…';
    readonly categoryLabelFont = '12px Poppins, Arial, sans-serif';
    readonly categoryLabelMinWidth = 120;
    readonly categoryLabelLineHeightPercent = 85;

    private measureContext: CanvasRenderingContext2D;

    constructor(
        series: ChartSeries[],
        config: Am5CategoryValueXYColumnChartConfiguration,
        settings: Am5CategoryValueXYColumnChartSettings
    ) {
        super(series, config, settings);
    }

    public override initChart(element: HTMLElement, chartType = XYChart): void {
        super.initChart(element, chartType);

        this.categoryAxis.events.on('boundschanged', () => {
            this.root.events.once('frameended', () => this.refreshColumnWidthDependentSettings());
        });
    }

    createCategoryAxisRenderer(rendererSettings: IAxisRendererYSettings): AxisRendererX {
        rendererSettings.cellStartLocation = 0.2;
        rendererSettings.cellEndLocation = 0.8;
        const renderer = AxisRendererX.new(this.root, rendererSettings);

        const label = renderer.labels.template;
        label.set('centerY', am5.p50);
        label.set('textAlign', 'center');
        label.set('oversizedBehavior', 'wrap');
        label.set('paddingTop', this.categoryLabelPaddingTop);
        label.set('lineHeight', am5.percent(this.categoryLabelLineHeightPercent));
        label.adapters.add('rotation', () => this.getCategoryLabelRotation());
        label.adapters.add('centerX', () => this.getCategoryLabelCenterX());
        label.adapters.add('maxWidth', () => this.getCategoryLabelMaxWidth());
        label.adapters.add('text', (text) => this.truncateCategoryLabelText(text));

        return renderer;
    }

    createValueAxisRenderer(rendererSettings: IAxisRendererYSettings): AxisRendererY {
        const renderer = AxisRendererY.new(this.root, rendererSettings);

        return renderer;
    }

    addAm5Series(chartSeries: ChartSeries, seriesIndex: number, settings: any): ColumnSeries {
        const setting = Object.assign(
            settings,
            {
                xAxis: this.categoryAxis,
                yAxis: this.valueAxis,
                categoryXField: 'label',
                valueYField: 'value' + seriesIndex
            }
        );
        const series = ColumnSeries.new(this.root, setting);
        series.columns.template.setAll({
            cornerRadiusTL: 2,
            cornerRadiusBL: 2,
            cornerRadiusTR: 2,
            cornerRadiusBR: 2
        });

        return series;
    }

    public override updateData(data: DataPointModel[]): void {
        super.updateData(data);
        this.root.events.once('frameended', () => this.refreshColumnWidthDependentSettings());
    }


    modifyDataArray(): DataPointModel[] {
        let dataPoints = super.modifyDataArray();
        const displayLimit = this.config.barDisplayLimit || 12;
        dataPoints = dataPoints.slice(0, displayLimit);
        return dataPoints;
    }

    refreshColumnWidthDependentSettings(): void {
        const label = this.categoryAxis.get('renderer').labels.template;
        label.set('rotation', this.getCategoryLabelRotation());
        label.set('centerX', this.getCategoryLabelCenterX());
        label.set('maxWidth', this.getCategoryLabelMaxWidth());
    }

    getColumnWidthRatio(): number {
        const paddedWidth = Math.max(this.getColumnWidth() - this.columnWidthPadding, 0);
        const clampedWidth = Math.min(paddedWidth, this.maxColumnWidthForRotation);

        return clampedWidth / this.maxColumnWidthForRotation;
    }

    getCategoryLabelRotation(): number {
        return -90 + this.getColumnWidthRatio() * 90;
    }

    getCategoryLabelCenterX(): am5.Percent {
        return am5.percent((1 - 0.5 * this.getColumnWidthRatio()) * 100);
    }

    getCategoryLabelMaxWidth(): number | undefined {
        return this.getColumnWidthRatio() > 0 ? Math.max(this.getColumnWidth(), this.categoryLabelMinWidth) : undefined;
    }

    truncateCategoryLabelText(text: string): string {
        const maxWidth = this.getCategoryLabelMaxWidth();
        if (!text || !maxWidth) {
            return text;
        }

        const maxTextWidth = maxWidth * this.categoryLabelMaxLines;
        if (this.getTextWidth(text) <= maxTextWidth) {
            return text;
        }

        const words = text.split(' ');
        let truncated = '';
        for (const word of words) {
            const candidate = truncated ? `${truncated} ${word}` : word;
            if (this.getTextWidth(candidate + this.categoryLabelEllipsis) > maxTextWidth) {
                break;
            }
            truncated = candidate;
        }
        if (!truncated) {
            // Even the first word alone doesn't fit - hard-cut it by character instead.
            truncated = text;
            while (truncated.length > 1 && this.getTextWidth(truncated + this.categoryLabelEllipsis) > maxTextWidth) {
                truncated = truncated.slice(0, -1);
            }
        }

        return truncated + this.categoryLabelEllipsis;
    }

    getTextWidth(text: string): number {
        if (!this.measureContext) {
            this.measureContext = document.createElement('canvas').getContext('2d');
            this.measureContext.font = this.categoryLabelFont;
        }

        return this.measureContext.measureText(text).width;
    }

    getColumnWidth(): number {
        const renderer = this.categoryAxis?.get('renderer') as AxisRendererX;
        if (!renderer) {
            return 0;
        }
        const columnCount = this.categoryAxis.dataItems.length || 1;

        return renderer.axisLength() / columnCount;
    }

    public clone(): Am5CategoryValueXYColumnChart {
        const clone = new Am5CategoryValueXYColumnChart(
            this.series.map(s => JSON.parse(JSON.stringify(s))),
            this.config,
            this.settings
        );

        clone.series.forEach((s, index) => {
            s.getSeriesUrl = this.series[index].getSeriesUrl?.bind(s);
        });

        return clone;
    }
}
